/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, DOCUMENT, Component, Inject, OnDestroy} from '@angular/core';
import {UIRouter} from '@uirouter/core';
import * as _ from 'lodash';
import * as moment from 'moment';

import template from './base-shell-layout.html';
import BUILD_INFO from '../../gen-config/build-info';
import {AUTH_EVENT, PERMISSIONS} from '../../components/auth/auth-constants';
import {ApiConfig} from '../../components/api/api';
import {getRegisteredUrl} from '../config/registered-urls';
import {SESSION_EVENT} from '../common/constants/misc-constants';
import {AppShellStateService} from './app-shell-state.service';
import {ReleaseNotesHelper} from '../../components/util/release-notes-helper.service';
import {ReleaseNotesModalService} from './simple/release-notes-modal.service';
import {UserProfileResource} from '../../components/api/resources/resource/user-profile.resource';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {LocalUserProfile} from '../../components/session/local-user-profile';
import {BrowserLocationService, WINDOW} from '../angular-platform-providers';
import {deferMarkForCheck} from '../common/util/defer-mark-for-check';
import {AppEventBusService} from '../common/events/app-event-bus.service';

@Component({
  standalone: false,
  selector: 'adn-base-shell-layout',
  template
})
export class BaseShellLayoutComponent implements OnDestroy {
  masq: any = {};
  isSelfServiceOwn = false;
  specialPanelVisible = false;
  showNetworks = false;
  showNotifications = false;
  showLast = false;
  ignoreClearOut = false;
  animationClass = '';
  showReleaseNotesNotification = false;
  permissions: any = {};
  env: string;
  uiLogo: string;
  releaseId: string;
  /** Copied from shell when networks load — avoids NG0100 on service getters. */
  appNetworkCount = 0;

  private localUser: any;
  private deregisterTransition: () => void;
  private destroyNetworkChangeListener: () => void;
  private destroyNetworksLoadedListener: () => void;
  private destroyUserProfileUpdateListener: () => void;
  private onDocumentClick: (event: MouseEvent) => void;
  private onDocumentKeydown: (event: KeyboardEvent) => void;
  private pageEnterClearTimer: ReturnType<typeof setTimeout> | undefined;
  private pageEnterRaf = 0;

  constructor(
    readonly shell: AppShellStateService,
    private readonly eventBus: AppEventBusService,
    @Inject(BrowserLocationService) private readonly location: any,
    @Inject(WINDOW) private readonly window: any,
    @Inject(DOCUMENT) private readonly document: any,
    private readonly uiRouter: UIRouter,
    private readonly releaseNotesHelper: ReleaseNotesHelper,
    private readonly releaseNotesModalService: ReleaseNotesModalService,
    @Inject(UserProfileResource) private readonly UserProfile: any,
    @Inject(LocalUserProfile) private readonly localUserProfile: any,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.init();
  }

  toggleNotifications(): void {
    this.doToggling('showNotifications');
  }

  toggleLast(): void {
    this.doToggling('showLast');
  }

  toggleNetworks(): void {
    this.doToggling('showNetworks');
  }

  toggleChangelog(): void {
    this.releaseNotesModalService.open(this.getReleaseNoteApps());

    this.localUser = this.localUserProfile.get();
    this.localUser.profile.adnPrefs.lastReleaseNotesRead = moment().unix();
    this.UserProfile.save({
      id: this.localUser.userId,
      profile: {
        adnPrefs: this.localUser.profile.adnPrefs
      }
    }, (apiUser: any) => {
      this.localUser = this.localUserProfile.safeSaveProfile('adnPrefs', apiUser.profile.adnPrefs);
      this.setReleaseNotesNotification(false);
    });
  }

  clearOut(): void {
    if (this.ignoreClearOut) {
      return;
    }
    this.emptyOut();
    this.evaluateVisibility();
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    if (this.deregisterTransition) {
      this.deregisterTransition();
    }
    if (this.destroyNetworkChangeListener) {
      this.destroyNetworkChangeListener();
    }
    if (this.destroyNetworksLoadedListener) {
      this.destroyNetworksLoadedListener();
    }
    if (this.destroyUserProfileUpdateListener) {
      this.destroyUserProfileUpdateListener();
    }
    if (this.onDocumentClick) {
      this.document.removeEventListener('click', this.onDocumentClick);
    }
    if (this.onDocumentKeydown) {
      this.document.removeEventListener('keydown', this.onDocumentKeydown);
    }
    this.clearPageEnterTimers();
  }

  private init(): void {
    const userPermissions = this.LocalUserPermissions.get();

    this.masq = {
      supportUserId: this.localUserProfile.get().supportUserId,
      username: this.localUserProfile.get().username
    };
    this.isSelfServiceOwn = this.LocalUserPermissions.isSelfServiceOwn();

    const regUrl = getRegisteredUrl(this.window, this.LocalNetworkProfile.get('networkId'));
    if (regUrl && regUrl.favicon) {
      const favicon = this.document.getElementById('favicon');
      if (favicon) {
        favicon.href = regUrl.favicon.href;
      }
    }

    this.destroyNetworksLoadedListener = this.shell.onNetworksChange(() => {
      // Copy onto the component; AdnHttp's view refresh will schedule zoneless CD.
      // Do not detectChanges() here — that walks ui-view content and NG0100s
      // in-flight targeting/list resolved flags.
      this.appNetworkCount = this.shell.appNetworks.length;
      this.cdr.markForCheck();
    });
    this.shell.bootstrap();

    this.localUser = this.localUserProfile.get();
    if (!this.localUser) {
      return;
    }

    // Do not set the badge true before the query — that true→false race is NG0100
    // on the changelog <a> when checkReleaseNotes finds nothing new.
    if (_.get(this.localUser, ['profile', 'adnPrefs', 'showReleaseHighlight'], 'SHOW') === 'SHOW') {
      this.checkReleaseNotes();
    }

    this.deregisterTransition = this.uiRouter.transitionService.onSuccess({}, () => {
      this.shell.refreshShellState();
      this.playPageEnter();
    }) as () => void;

    this.clearOut();

    if (_.startsWith(this.location.hostname, 'localhost') && !_.startsWith(ApiConfig.obtain().getId(), 'localhost')) {
      this.env = ApiConfig.obtain().getId().split('_')[0].toUpperCase();
    }

    this.permissions = _.mapValues(PERMISSIONS, (permission) => {
      return userPermissions && userPermissions.hasPermission(permission.apiKey);
    });

    this.uiLogo = _.get(regUrl, ['logo']) || this.LocalNetworkProfile.get('adnUiLogo');
    this.releaseId = BUILD_INFO.gitSha.substring(0, 10);

    this.destroyNetworkChangeListener = this.eventBus.on(AUTH_EVENT.networkChange, (_eventData: any, network: any) => {
      this.shell.syncCurrentNetworkFromAuthEvent(network);
      const updatedRegUrl = getRegisteredUrl(this.window, network.id);
      this.uiLogo = _.get(updatedRegUrl, ['logo']) || this.LocalNetworkProfile.get('adnUiLogo');
    });

    this.destroyUserProfileUpdateListener = this.eventBus.on(SESSION_EVENT.userProfileUpdate, () => {
      this.localUser = this.localUserProfile.get();
      const newReleaseHighlight = _.get(this.localUser, ['profile', 'adnPrefs', 'showReleaseHighlight'], 'SHOW');
      if (newReleaseHighlight === 'HIDE' && this.showReleaseNotesNotification) {
        this.setReleaseNotesNotification(false);
      }
      if (newReleaseHighlight === 'SHOW' && !this.showReleaseNotesNotification) {
        this.checkReleaseNotes();
      }
    });

    this.onDocumentClick = (event: MouseEvent) => {
      // ignoreClearOut covers the same click that opened/switched the panel
      // (that click bubbles here before *ngIf has painted .specialPanel).
      if (!this.specialPanelVisible || this.ignoreClearOut) {
        return;
      }
      const panel = this.document.querySelector('.specialPanel');
      if (!panel || panel.contains(event.target as Node)) {
        return;
      }
      this.clearOut();
    };
    this.onDocumentKeydown = (event: KeyboardEvent) => {
      if (event.keyCode === 27 && this.specialPanelVisible) {
        this.clearOut();
      }
    };

    this.document.addEventListener('click', this.onDocumentClick);
    this.document.addEventListener('keydown', this.onDocumentKeydown);
  }

  private getReleaseNoteApps(): string[] {
    const tags = [];
    if (this.LocalNetworkProfile.isMarketplacePlatform()) {
      tags.push('MARKETPLACE');
    }
    if (!this.LocalNetworkProfile.isPureMarketplacePlatform()) {
      tags.push('ADSERVER');
    }
    return tags;
  }

  private checkReleaseNotes(): void {
    const lastRead = _.get(this.localUser, ['profile', 'adnPrefs', 'lastReleaseNotesRead']);
    this.releaseNotesHelper.query(this.getReleaseNoteApps(), true, 'ACTIVE', lastRead).then((data: any[]) => {
      this.setReleaseNotesNotification(data.length > 0);
    });
  }

  /** Badge *ngIf must not flip during an in-flight CD verify pass (NG0100). */
  private setReleaseNotesNotification(visible: boolean): void {
    deferMarkForCheck(this.cdr, () => {
      this.showReleaseNotesNotification = visible;
    });
  }

  private emptyOut(): void {
    this.specialPanelVisible = false;
    this.showNetworks = false;
    this.showNotifications = false;
    this.showLast = false;
  }

  private evaluateVisibility(): void {
    this.specialPanelVisible = this.showNetworks || this.showNotifications || this.showLast;
  }

  private doToggling(param: 'showNetworks' | 'showNotifications' | 'showLast'): void {
    if (this.specialPanelVisible && !this[param]) {
      this.ignoreClearOut = true;
    }
    if (this[param]) {
      this.emptyOut();
      this.cdr.markForCheck();
      return;
    }
    this.ignoreClearOut = true;
    this.emptyOut();
    this[param] = true;
    this.evaluateVisibility();
    this.cdr.markForCheck();
    // Keep ignoreClearOut through the opening click's document bubble, then allow dismiss.
    setTimeout(() => {
      this.ignoreClearOut = false;
    });
  }

  /** Re-trigger CSS enter animation after each successful route change. */
  private playPageEnter(): void {
    this.clearPageEnterTimers();
    this.animationClass = '';
    this.cdr.markForCheck();
    this.pageEnterRaf = requestAnimationFrame(() => {
      this.animationClass = 'adn-page-enter';
      this.cdr.markForCheck();
      this.pageEnterClearTimer = setTimeout(() => {
        if (this.animationClass === 'adn-page-enter') {
          this.animationClass = '';
          this.cdr.markForCheck();
        }
      }, 160);
    });
  }

  private clearPageEnterTimers(): void {
    if (this.pageEnterRaf) {
      cancelAnimationFrame(this.pageEnterRaf);
      this.pageEnterRaf = 0;
    }
    if (this.pageEnterClearTimer) {
      clearTimeout(this.pageEnterClearTimer);
      this.pageEnterClearTimer = undefined;
    }
  }
}
